import { useFlag } from '@unleash/proxy-client-react';
import { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { singleProductSelectors } from '../../../store/single-product';
import { RootState } from '../../../store/store';
import { Product } from '../../../types';
import {
  StyledModal,
  ModalContent,
  Title,
  Subtitle,
  CloseModalButton,
  CloseModalIcon,
  ModalBody,
  BodySection,
  SectionColumn,
  SectionDivider,
  DataLabel,
  ModalFooter,
  Spinner,
  CloseButton,
  EditButton,
  DataValue,
  SectionDividerMarginTop
} from './product-details-modal.styles';
import appConfig from '../../../config/app.config';
import { TooltipLongText2Rows, TooltipShortText } from '../common/tooltip.styles';
import { StringPanelAccordion } from '../accordion-panels/string-panel-accordion';
import { capitalize } from 'lodash';
import { ModifiersPanel } from '../accordion-panels/modifiers-panel';
import { HealthClaimsPanel } from '../accordion-panels/health-claims-panel';
import { EnlargeImageModal } from './enlarge-image-modal';

function valueToDisplay(value: string | number | boolean | null | undefined) {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return !value && value !== 0 ? 'N/A' : value;
}

const TextDataItem: FC<{
  id: string;
  label: string;
  value: string | number | boolean | null;
}> = ({ id, label, value }) => {
  let val = valueToDisplay(value);

  return (
    <div>
      <DataLabel id={`${id}-label`}> {label} </DataLabel>
      <DataValue id={id}>{val}</DataValue>
    </div>
  );
};

interface ModalProps {
  onClose: () => void;
  isOpen: boolean;
  onEditClick: () => void;
}

export const ProductDetailsModal: FC<ModalProps> = ({ onClose, isOpen, onEditClick }) => {
  const isEditProductEnabled: boolean = useFlag(appConfig.FEATURE_FLAGS.FEATURES.EDIT_PRODUCT);
  const [expanded, setExpanded] = useState<string | false>();

  const handleChange =
    (panel: string, bodyLength: number) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      bodyLength > 0 && setExpanded(newExpanded ? panel : false);
    };

  const panels = [
    <ModifiersPanel
      key='modifiers-panel'
      expanded={expanded}
      handleAccordionChange={handleChange}
    />,
    <HealthClaimsPanel
      key='health-claims-panel'
      expanded={expanded}
      handleAccordionChange={handleChange}
    />
  ];

  const product: Product = useSelector((state: RootState) =>
    singleProductSelectors.selectProduct(state.singleProduct)
  );

  const isModalLoading: boolean = useSelector((state: RootState) =>
    singleProductSelectors.selectIsModalLoading(state.singleProduct)
  );

  if (isModalLoading) {
    return (
      <StyledModal onClose={onClose} open={isOpen} id='product-modal'>
        <ModalContent id='product-modal-content'>
          <Spinner />
        </ModalContent>
      </StyledModal>
    );
  }

  return (
    <StyledModal onClose={onClose} open={isOpen} id='product-modal'>
      <ModalContent id='product-modal-content'>
        <CloseModalButton onClick={onClose} id='product-modal-top-close-button'>
          <CloseModalIcon />
        </CloseModalButton>
        <Title id='product-modal-main-title'>Product details</Title>
        <Subtitle id='product-modal-subtitle'>
          {product.tenantName} - {product.locationName} - {product.pricelistName}
        </Subtitle>
        <ModalBody id='product-modal-body'>
          <BodySection id='product-modal-body-section'>
            <SectionColumn width={'180px'}>
              <DataLabel id='product-modal-image-label'> Product image </DataLabel>
              <EnlargeImageModal product={product} />
            </SectionColumn>
            <SectionColumn width={'200px'}>
              <DataLabel id={'product-modal-display-name-label'}>Product display name</DataLabel>
              <TooltipShortText
                id='product-modal-display-name'
                value={valueToDisplay(product.displayName) as 'string'}
                minCharacters={25}
                child={
                  <DataValue id='product-modal-display-name'>
                    {valueToDisplay(product.displayName)}
                  </DataValue>
                }
              />
              <TextDataItem
                id='product-modal-upc'
                label='UPC / Identify value'
                value={product.upc}
              />
              <TextDataItem
                id='product-modal-product-brand'
                label='Product brand'
                value={product.brand}
              />
            </SectionColumn>
            <SectionColumn width={'200px'}>
              <TextDataItem
                id='product-modal-main-category'
                label='Main category'
                value={capitalize(product.type)}
              />
              <TextDataItem
                id='product-modal-sub-category'
                label='Sub category'
                value={capitalize(product.subType)}
              />
              <DataLabel id='product-modal-product-catalog-name-label'>
                Product catalog name
              </DataLabel>
              <TooltipShortText
                id='product-modal-product-catalog-name'
                value={valueToDisplay(product.productName) as 'string'}
                minCharacters={25}
                child={
                  <DataValue id='product-modal-product-catalog-name'>
                    {valueToDisplay(product.productName)}
                  </DataValue>
                }
              />
            </SectionColumn>
            <SectionColumn width={'180px'}>
              <TextDataItem
                id='product-modal-unit-quantity'
                label='Unit quantity'
                value={product.unitQuantity}
              />
              <TextDataItem
                id='product-modal-min-quantity'
                label='Min order quantity'
                value={product.minQuantity}
              />
              <TextDataItem
                id='product-modal-max-quantity'
                label='Max order quantity'
                value={product.maxQuantity}
              />
            </SectionColumn>
            <SectionColumn width={'180px'}>
              <TextDataItem id='product-modal-unit-size' label='Unit size' value={product.unit} />
              <TextDataItem
                id='product-modal-unit-increment'
                label='Unit increment'
                value={product.unitIncrement}
              />
              <TextDataItem id='product-modal-price' label='Price' value={'$' + product.price} />
            </SectionColumn>
          </BodySection>
          <SectionDivider />
          <BodySection>
            <SectionColumn width={'180px'}>
              <TextDataItem id='product-modal-organic' label='Organic' value={product.organic} />
              <TextDataItem
                id='product-modal-in-stock'
                label='In stock'
                value={product.isInStock}
              />
              <TextDataItem id='product-modal-taxable' label='Taxable' value={product.isTaxable} />
            </SectionColumn>
            <SectionColumn width={'200px'}>
              <TextDataItem
                id='product-modal-alcoholic'
                label='Alcoholic'
                value={product.isAlcohol}
              />
              <TextDataItem
                id='product-modal-is-active'
                label='Active'
                value={product.isInactive === null ? null : !product.isInactive}
              />
              <TextDataItem
                id='product-modal-pickup-only'
                label='Pickup only'
                value={product.isPickupOnly}
              />
            </SectionColumn>
            <SectionColumn width={'200px'}>
              <TextDataItem
                id='product-modal-sell-by-quantity'
                label='Sell by quantity?'
                value={product.sellByQty}
              />
              <TextDataItem
                id='product-modal-is-on-homepage'
                label='On homepage?'
                value={product.isOnHomepage}
              />
            </SectionColumn>
            <SectionColumn width={'375px'}>
              <DataLabel id={'product-modal-ingredients-label'}>Ingredients</DataLabel>
              <TooltipLongText2Rows
                id='product-modal-ingredients'
                value={valueToDisplay(product.ingredients) as 'string'}
                minCharacters={100}
                child={
                  <DataValue id='product-modal-ingredients'>
                    {valueToDisplay(product.ingredients)}
                  </DataValue>
                }
              />
              <DataLabel id={'product-modal-disclaimer-label'}>Description</DataLabel>
              <TooltipLongText2Rows
                id='product-modal-description'
                value={valueToDisplay(product.description) as 'string'}
                minCharacters={100}
                child={
                  <DataValue id='product-modal-description'>
                    {valueToDisplay(product.description)}
                  </DataValue>
                }
              />
              <DataLabel id={'product-modal-disclaimer-label'}>Disclaimer</DataLabel>
              <TooltipLongText2Rows
                id='product-modal-disclaimer'
                value={valueToDisplay(product.disclaimer) as 'string'}
                minCharacters={100}
                child={
                  <DataValue id='product-modal-disclaimer'>
                    {valueToDisplay(product.disclaimer)}
                  </DataValue>
                }
              />
            </SectionColumn>
          </BodySection>
          <SectionDividerMarginTop />
          <StringPanelAccordion panels={panels} />
        </ModalBody>
        <ModalFooter id='product-modal-footer'>
          <CloseButton
            id='product-modal-bottom-close-button'
            variant='outlined'
            onClick={onClose}
            style={isEditProductEnabled ? {} : { marginLeft: '905px' }}
          >
            Close
          </CloseButton>
          {isEditProductEnabled && (
            <EditButton id='product-modal-edit-button' onClick={onEditClick}>
              Edit
            </EditButton>
          )}
        </ModalFooter>
      </ModalContent>
    </StyledModal>
  );
};
